import { makeObservable } from 'mobx';
import { WithdrawPayload } from '@monorepo/client-types';
import {
  AirwallexBeneficiaryFormOptions,
  PaginationRequest,
  PaginationResult,
  PaymentMethod,
  Payout,
  PayoutSource,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';

export default class PayoutStore {
  constructor() {
    makeObservable(this);
  }

  async payouts(
    type: PayoutSource,
    sourceId: string,
    paginateRequest: PaginationRequest,
  ): Promise<PaginationResult<Payout>> {
    try {
      const {
        apiFactory: { payoutService },
      } = getEnv();
      return await payoutService.getPayouts(type, +sourceId, paginateRequest);
    } catch (e) {
      console.error(e);
      return { total: 0, results: [] };
    }
  }

  async getPayoutTotalForSource(values: {
    type: PayoutSource;
    sourceId: string;
  }) {
    try {
      const {
        apiFactory: { payoutService },
      } = getEnv();
      return await payoutService.getPayoutsTotal(values.type, +values.sourceId);
    } catch (e) {
      console.error(e);
      return 0;
    }
  }

  async createPayout(values: {
    amount: number;
    paymentOption: PaymentMethod;
    source: PayoutSource;
  }) {
    try {
      const {
        apiFactory: { payoutService },
      } = getEnv();
      return await payoutService.createPayout(
        values.source,
        values.amount,
        values.paymentOption,
      );
    } catch (e) {
      console.error(e);
      return { total: 0, results: [] };
    }
  }

  async createPayoutRequest(
    payload: WithdrawPayload & {
      source: PayoutSource;
      sourceId: string;
    },
  ) {
    try {
      const {
        apiFactory: { payoutService },
      } = getEnv();

      return await payoutService.createPayoutRequest(payload);
    } catch (e) {
      console.error(e);
    }
  }

  async eligibleForWithdraw(values: {
    source: PayoutSource;
    sourceId: string;
  }) {
    try {
      const {
        apiFactory: { payoutService },
      } = getEnv();
      return await payoutService.getIsEligible(values.source, values.sourceId);
    } catch (e) {
      console.error(e);
      return {
        isValid: false,
        availableAmount: 0,
      };
    }
  }

  async getRiseId() {
    const {
      apiFactory: { payoutService },
    } = getEnv();

    return payoutService.getRiseId();
  }

  async sendRiseInvitation() {
    const {
      apiFactory: { payoutService },
    } = getEnv();

    return payoutService.sendRiseInvitation();
  }

  async getAirwallexAuth() {
    const {
      apiFactory: { payoutService },
    } = getEnv();

    try {
      return await payoutService.getAirwallexAuth();
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async createAirwallexBeneficiary(
    airwallexForm: AirwallexBeneficiaryFormOptions,
  ) {
    const {
      apiFactory: { payoutService },
    } = getEnv();

    try {
      return await payoutService.createAirwallexBeneficiary(airwallexForm);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }
}
